import { Link, useParams } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { SiteFooter, SiteNav } from "@/components/media/SiteNav";
import { Eyebrow, LessonCard, Section, StoryCard } from "@/components/media/Sections";
import { JourneyFeed } from "@/components/media/Blocks";
import { ApplyForm, ApplyIntro } from "@/components/media/ApplyForm";
import { VideoEmbed } from "@/components/media/VideoEmbed";
import { ShareButton, useShareMeta } from "@/components/media/Share";
import { OperatorPackages } from "@/components/media/Packages";
import { LicenseBlock } from "@/components/media/License";
import { ArrowLeft, ArrowRight } from "lucide-react";

const Loading = () => (
  <div>
    <SiteNav />
    <div className="mono px-5 py-32 text-center text-sm text-muted-foreground">Loading…</div>
  </div>
);

const NotFound = ({ backTo, backLabel }) => (
  <div data-testid="not-found">
    <SiteNav />
    <div className="mx-auto max-w-lg px-5 py-32 text-center">
      <h1 className="neon text-4xl">Not here yet</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        This one hasn't been published. It may still be filming.
      </p>
      <Link to={backTo} className="mono mt-8 inline-block text-[11px] tracking-widest text-primary hover:underline">
        ← {backLabel}
      </Link>
    </div>
    <SiteFooter />
  </div>
);

/** LEMONADING — all entrepreneur stories. */
export function Lemonading() {
  const site = useSite();
  if (!site) return <Loading />;
  const stories = site.stories || [];

  return (
    <div data-testid="lemonading-page">
      <SiteNav />
      <Section testId="lemonading-hero">
        <Eyebrow>THE MOVEMENT</Eyebrow>
        <h1 className="neon mt-3 text-5xl leading-none sm:text-6xl lg:text-7xl">
          {site.lemonading.title}
        </h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white md:text-2xl">
          {site.lemonading.definition}
        </p>
        <p className="brand-script mt-8 max-w-2xl text-2xl text-primary md:text-3xl">
          “{site.lemonading.question}”
        </p>

        {stories.length ? (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {stories.map((s) => (
              <StoryCard key={s.id} story={s} />
            ))}
          </div>
        ) : (
          <p className="mono mt-10 text-[11px] tracking-widest text-muted-foreground">
            {site.lemonading.empty_note}
          </p>
        )}
      </Section>
      <SiteFooter />
    </div>
  );
}

/** STORIES — the vertical clip feed. */
export function Stories() {
  const site = useSite();
  if (!site) return <Loading />;
  const clips = site.journeyClips || [];

  return (
    <div data-testid="stories-page">
      <SiteNav />
      <Section testId="stories-hero">
        {clips.length ? (
          <JourneyFeed
            journey={site.smashJourney}
            clips={clips}
            categories={site.clip_categories || []}
          />
        ) : (
          <>
            <Eyebrow>STRAIGHT FROM THE TENT</Eyebrow>
            <h1 className="neon mt-3 text-4xl leading-tight sm:text-5xl">
              {site.smashJourney.title}
            </h1>
            <p className="mono mt-8 text-[11px] tracking-widest text-muted-foreground">
              THE FIRST CLIPS ARE BEING PUBLISHED NOW.
            </p>
          </>
        )}
      </Section>
      <SiteFooter />
    </div>
  );
}

const renderBody = (body = "") =>
  body.split("\n\n").map((para) => {
    const bold = para.match(/^\*\*(.+?)\*\*\s*(.*)$/s);
    if (bold)
      return (
        <p key={para.slice(0, 24)} className="text-base leading-relaxed text-muted-foreground">
          <strong className="text-white">{bold[1]}</strong> {bold[2]}
        </p>
      );
    return (
      <p key={para.slice(0, 24)} className="text-base leading-relaxed text-muted-foreground">
        {para}
      </p>
    );
  });

/** Permanent shareable page for a single entrepreneur story. */
export function StoryPage() {
  const { slug } = useParams();
  const site = useSite();
  const story = (site?.stories || []).find((s) => s.slug === slug);
  useShareMeta({
    title: story ? `${story.name} — ${story.business}` : "",
    description: story?.lemon || story?.built || "",
    image: story?.thumbnail,
  });
  if (!site) return <Loading />;
  if (!story) return <NotFound backTo="/lemonading" backLabel="ALL STORIES" />;
  const others = (site.stories || []).filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`story-page-${slug}`}>
      <SiteNav />
      <Section testId="story-hero">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/lemonading" className="mono inline-flex items-center gap-1 text-[10px] tracking-widest text-muted-foreground hover:text-primary">
            <ArrowLeft size={12} /> LEMONADING
          </Link>
          <ShareButton kind="story" slug={slug} title={story.name} testId="story-share" />
        </div>
        <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,380px)_1fr]">
          <div>
            <VideoEmbed
              url={story.video_url}
              poster={story.thumbnail}
              testId="story-main-video"
              aspect="9/16"
            />
          </div>
          <div>
            <h1 className="neon text-4xl leading-none sm:text-5xl">{story.name}</h1>
            <div className="mono mt-2 text-[11px] tracking-widest text-primary">
              {[story.business, story.city].filter(Boolean).join(" · ")}
            </div>
            {story.lemon ? (
              <div className="mt-8 border-l-2 border-primary pl-4">
                <div className="mono text-[10px] tracking-widest text-muted-foreground">
                  THE LEMON
                </div>
                <p className="mt-1 text-lg text-white md:text-xl">{story.lemon}</p>
              </div>
            ) : null}
            {story.built ? (
              <div className="mt-6 border-l-2 border-border pl-4">
                <div className="mono text-[10px] tracking-widest text-muted-foreground">
                  WHAT THEY BUILT
                </div>
                <p className="mt-1 text-lg text-white md:text-xl">{story.built}</p>
              </div>
            ) : null}
            <div className="mt-8 space-y-4">{renderBody(story.body)}</div>
          </div>
        </div>
      </Section>

      {others.length ? (
        <Section testId="story-more" tint>
          <Eyebrow>MORE LEMONADING</Eyebrow>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((s) => (
              <StoryCard key={s.id} story={s} />
            ))}
          </div>
        </Section>
      ) : null}
      <SiteFooter />
    </div>
  );
}

/** LEARN — free education index. */
export function Learn() {
  const site = useSite();
  if (!site) return <Loading />;

  return (
    <div data-testid="learn-page">
      <SiteNav />
      <Section testId="learn-hero">
        <Eyebrow>TEACH FIRST</Eyebrow>
        <h1 className="neon mt-3 text-4xl leading-tight sm:text-5xl lg:text-6xl">
          {site.lessonsIntro.title}
        </h1>
        <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
          {site.lessonsIntro.intro}
        </p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(site.lessons || []).map((l) => (
            <LessonCard key={l.id} lesson={l} />
          ))}
        </div>
      </Section>
      <SiteFooter />
    </div>
  );
}

/** Permanent shareable page for a single free lesson. */
export function LessonPage() {
  const { slug } = useParams();
  const site = useSite();
  const lessons = site?.lessons || [];
  const lesson = lessons.find((l) => l.slug === slug);
  useShareMeta({
    title: lesson?.title || "",
    description: lesson?.blurb || "",
    image: "/brand/cover.jpg",
  });
  if (!site) return <Loading />;
  if (!lesson) return <NotFound backTo="/learn" backLabel="ALL LESSONS" />;
  const next = lessons[(lessons.findIndex((l) => l.slug === slug) + 1) % lessons.length];

  return (
    <div data-testid={`lesson-page-${slug}`}>
      <SiteNav />
      <Section testId="lesson-hero" className="max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/learn" className="mono inline-flex items-center gap-1 text-[10px] tracking-widest text-muted-foreground hover:text-primary">
            <ArrowLeft size={12} /> ALL LESSONS
          </Link>
          <ShareButton kind="lesson" slug={slug} title={lesson.title} testId="lesson-share" />
        </div>
        <div className="mono mt-6 text-[10px] tracking-widest text-primary">
          FREE LESSON {String(lesson.order).padStart(2, "0")}
        </div>
        <h1 className="neon mt-2 text-4xl leading-tight sm:text-5xl">{lesson.title}</h1>
        <p className="mt-4 text-lg text-white">{lesson.blurb}</p>

        {lesson.video_url ? (
          <div className="mt-10">
            <VideoEmbed url={lesson.video_url} testId="lesson-video" aspect="16/9" />
          </div>
        ) : null}

        <div className="mt-10 space-y-5">{renderBody(lesson.body)}</div>

        <div className="mt-14 border border-border bg-[#141414] p-6">
          <div className="mono text-[10px] tracking-widest text-muted-foreground">NEXT LESSON</div>
          <Link
            to={`/learn/${next.slug}`}
            data-testid="lesson-next"
            className="display mt-2 inline-flex items-center gap-2 text-2xl uppercase text-primary hover:underline"
          >
            {next.title} <ArrowRight size={16} />
          </Link>
        </div>

        <div className="mt-8 border border-primary/50 bg-[#191603] p-6">
          <p className="text-base text-white">
            Want all of it as one system instead of ten articles?
          </p>
          <Link
            to="/academy"
            data-testid="lesson-academy-cta"
            className="display mt-4 inline-flex rounded-sm bg-primary px-6 py-3 text-lg uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
          >
            See how Smash Academy works
          </Link>
        </div>
      </Section>
      <SiteFooter />
    </div>
  );
}

/** APPLY — the primary conversion. */
export function Apply() {
  const site = useSite();
  if (!site) return <Loading />;

  return (
    <div data-testid="apply-page">
      <SiteNav />
      {site.packages ? (
        <Section testId="packages-section" tint>
          <OperatorPackages packages={site.packages} />
        </Section>
      ) : null}
      <Section testId="license-section" id="license">
        <LicenseBlock license={site.license} />
      </Section>
      <Section testId="apply-hero" className="max-w-3xl">
        <ApplyIntro apply={site.apply} />
        <div className="mt-10">
          <ApplyForm apply={site.apply} />
        </div>
      </Section>
      <SiteFooter />
    </div>
  );
}
